import { supabase } from '../lib/supabase';

/**
 * Service for managing planner configurations in Supabase
 * Handles snake_case (DB) ↔ camelCase (React) conversion
 */

// Helper to get authenticated user with retry logic
const getAuthenticatedUser = async (retries = 3, delay = 500) => {
  for (let i = 0; i < retries; i++) {
    try {
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();
      
      if (user && !userError) {
        return { user, error: null };
      }
      
      // If not authenticated and retries remain, wait and try again
      if (i < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    } catch (error) {
      console.error(`Auth check attempt ${i + 1} failed:`, error);
      if (i === retries - 1) {
        return { user: null, error };
      }
    }
  }
  
  return { user: null, error: new Error('Authentication timeout') };
};

// Convert DB row (snake_case) to React object (camelCase)
const convertToReactFormat = (dbRow) => {
  if (!dbRow) return null;
  
  return {
    id: dbRow?.id,
    userId: dbRow?.user_id,
    plannerName: dbRow?.planner_name,
    pageSize: dbRow?.page_size,
    layoutType: dbRow?.layout_type,
    colorScheme: dbRow?.color_scheme,
    isFavorite: dbRow?.is_favorite,
    configurationData: dbRow?.configuration_data,
    generatedPdfPath: dbRow?.generated_pdf_path,
    isFullPlanner: dbRow?.is_full_planner,
    createdAt: dbRow?.created_at,
    updatedAt: dbRow?.updated_at
  };
};

// Convert React object (camelCase) to DB format (snake_case)
const convertToDbFormat = (reactObj) => {
  return {
    planner_name: reactObj?.plannerName,
    page_size: reactObj?.pageSize,
    layout_type: reactObj?.layoutType,
    color_scheme: reactObj?.colorScheme,
    is_favorite: reactObj?.isFavorite || false,
    configuration_data: reactObj?.configurationData,
    generated_pdf_path: reactObj?.generatedPdfPath,
    is_full_planner: reactObj?.isFullPlanner || false
  };
};

export const plannerConfigService = {
  /**
   * Get all planner configurations for the authenticated user
   */
  async getAll() {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      console.warn('User not authenticated for getAll:', authError);
      return [];
    }

    const { data, error } = await supabase?.from('planner_configurations')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(convertToReactFormat) || [];
  },

  /**
   * Get a specific planner configuration by ID
   */
  async getById(configId) {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      console.warn('User not authenticated for getById:', authError);
      return null;
    }

    const { data, error } = await supabase?.from('planner_configurations')?.select('*')?.eq('id', configId)?.eq('user_id', user?.id)?.single();

    if (error) throw error;

    return convertToReactFormat(data);
  },

  /**
   * Create a new planner configuration
   */
  async create(configData) {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      throw new Error('Not authenticated. Please sign in again.');
    }

    // Prepare data for insertion
    const dbData = {
      user_id: user?.id,
      ...convertToDbFormat(configData)
    };

    const { data, error } = await supabase?.from('planner_configurations')?.insert(dbData)?.select()?.single();

    if (error) throw error;

    return convertToReactFormat(data);
  },

  /**
   * Update an existing planner configuration
   */
  async update(configId, configData) {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      throw new Error('Not authenticated. Please sign in again.');
    }

    const dbData = convertToDbFormat(configData);

    const { data, error } = await supabase?.from('planner_configurations')?.update(dbData)?.eq('id', configId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return convertToReactFormat(data);
  },

  /**
   * Delete a planner configuration
   */
  async delete(configId) {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      throw new Error('Not authenticated. Please sign in again.');
    }

    const { error } = await supabase?.from('planner_configurations')?.delete()?.eq('id', configId)?.eq('user_id', user?.id);

    if (error) throw error;

    return true;
  },

  /**
   * Toggle favorite status for a planner configuration
   */
  async toggleFavorite(configId, isFavorite) {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      throw new Error('Not authenticated. Please sign in again.');
    }

    const { data, error } = await supabase?.from('planner_configurations')?.update({ is_favorite: isFavorite })?.eq('id', configId)?.eq('user_id', user?.id)?.select()?.single();

    if (error) throw error;

    return convertToReactFormat(data);
  },

  /**
   * Get the most recent planner configuration (for autosave/restore)
   */
  async getLatest() {
    const { user, error: authError } = await getAuthenticatedUser();
    if (authError || !user) {
      console.warn('User not authenticated for getLatest:', authError);
      return null;
    }

    const { data, error } = await supabase?.from('planner_configurations')?.select('*')?.eq('user_id', user?.id)?.order('updated_at', { ascending: false })?.limit(1)?.maybeSingle();

    if (error) {
      console.error('Error fetching latest config:', error);
      return null;
    }

    return convertToReactFormat(data);
  }
};